import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaTimes } from 'react-icons/fa';

const products = [
  {
    id: 1,
    title: ' Business Loans',
    image: '/assets/images/loans/artboard1.png',
    imageSmall: '/assets/images/loans/artboard1-small.png',
  },
  {
    id: 2,
    title: 'Asset Financing',
    image: '/assets/images/loans/artboard2.png',
    imageSmall: '/assets/images/loans/artboard2-small.png',
  },
  {
    id: 3,
    title: 'LPO Financing',
    image: '/assets/images/loans/artboard3.png',
    imageSmall: '/assets/images/loans/artboard4-small.png',
  },
  {
    id: 4,
    title: 'Personal Loans',
    image: '/assets/images/loans/artboard4.png',
    imageSmall: '/assets/images/loans/artboard3-small.png',
  },
  {
    id: 5,
    title: 'Bid Bonds',
    image: '/assets/images/loans/artboard5.jpeg',
    imageSmall: '/assets/images/loans/artboard5-small.png',
  },
  {
    id: 6,
    title: 'Motor Insurance',
    image: '/assets/images/loans/artboard6.png',
    imageSmall: '/assets/images/loans/artboard6-small.png',
  },
];

const IconArrow = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

const FeaturedProducts = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
    service: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [transform, setTransform] = useState({});
  const rafId = useRef(null);
  const cardRefs = useRef({});
  const modalRef = useRef(null);

  // Synchronize formData.service with selectedProduct
  useEffect(() => {
    setFormData((prev) => ({ ...prev, service: selectedProduct }));
  }, [selectedProduct]);

  // Cleanup for mouse events
  useEffect(() => {
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      Object.values(cardRefs.current).forEach((card) => {
        if (card) {
          card.removeEventListener('mousemove', handleMouseMove);
          card.removeEventListener('mouseleave', handleMouseLeave);
        }
      });
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+?\d{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone number is invalid (10-15 digits)';
    }
    if (!formData.service.trim()) newErrors.service = 'Service is required';
    return newErrors;
  };

  const openModal = (title) => {
    console.log('Opening modal for product:', title);
    if (!title) {
      console.error('No product title provided');
      setErrors({ service: 'Service is required' });
      return;
    }
    setSelectedProduct(title);
    setFormData({
      name: '',
      email: '',
      address: '',
      phone: '',
      service: title, // Set service immediately
    });
    setIsModalOpen(true);
    setErrors({});
    setSubmitError('');
    setShowSuccessMessage(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct('');
    setFormData({ name: '', email: '', address: '', phone: '', service: '' });
    setErrors({});
    setSubmitError('');
    setShowSuccessMessage(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      console.log('Form validation errors:', formErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        address: formData.address.trim(),
        phone: formData.phone.trim(),
        service: formData.service.trim(), // Use formData.service directly
      };
      console.log('Sending payload:', payload);
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL || 'https://tglcredit.co.ke/api/product-email.php',
        payload,
        { headers: { 'Content-Type': 'application/json' } } // Ensure correct Content-Type
      );
      console.log('Email sent successfully:', response.data);
      setShowSuccessMessage(true);
      setTimeout(() => {
        closeModal();
      }, 3000);
    } catch (error) {
      console.error('Error sending email:', error);
      if (error.response) {
        setSubmitError(`Failed to send application: ${error.response.data.message || error.message}`);
      } else if (error.request) {
        setSubmitError('Failed to send application. Please check your connection or server availability.');
      } else {
        setSubmitError('Failed to send application. An unexpected error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMouseMove = useCallback((e, id) => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      const card = cardRefs.current[id];
      if (!card) {
        console.warn(`Card element for ID ${id} is null in handleMouseMove`);
        return;
      }
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      const maxTilt = 5;
      const translateX = (x / rect.width) * 8;
      const translateY = (y / rect.height) * 8;
      const rotateX = (y / rect.height) * maxTilt;
      const rotateY = -(x / rect.width) * maxTilt;
      setTransform((prev) => ({
        ...prev,
        [id]: `translate3d(${translateX}px, ${translateY}px, 0) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
      }));
    });
  }, []);

  const handleMouseLeave = useCallback((id) => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    setTransform((prev) => ({
      ...prev,
      [id]: 'translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg)',
    }));
  }, []);

  // Focus trap for modal
  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
        if (e.key === 'Escape') {
          closeModal();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      firstElement.focus();

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isModalOpen]);

  return (
    <>
      <section className="bg-gradient-to-b from-gray-50 to-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-inter font-extrabold text-center mb-6 animate-fade-up">
            <span className="text-gray-800 relative group inline-block">
              Our
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[--primary-red] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
            </span>{' '}
            <span className="text-[--primary-blue]">Products</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="relative bg-white rounded-lg shadow-md hover:shadow-lg border border-[--primary-blue]/10 transition-all duration-300 cursor-pointer hover:-translate-y-1 animate-fade-up"
                ref={(el) => (cardRefs.current[product.id] = el)}
                onMouseMove={(e) => handleMouseMove(e, product.id)}
                onMouseLeave={() => handleMouseLeave(product.id)}
                style={{ animationDelay: `${0.4 + index * 0.2}s`, willChange: 'transform, box-shadow' }}
              >
                <div className="relative overflow-visible rounded-t-lg group aspect-[4/3]">
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-[--primary-blue]/20 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-500 ease-in-out z-10"
                    style={{ willChange: 'opacity' }}
                  ></div>
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover rounded-t-lg transform transition-all duration-500 ease-in-out group-hover:scale-105 group-hover:brightness-105"
                    style={{
                      transform: transform[product.id] || 'translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg)',
                      transformStyle: 'preserve-3d',
                      willChange: 'transform, filter',
                      animationDelay: `${0.4 + index * 0.2}s`,
                    }}
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/assets/images/fallback.jpg';
                      console.error(`Failed to load image: ${product.image}`);
                    }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-inter font-bold text-[--primary-blue] mb-3 tracking-tight">{product.title}</h3>
                  <button
                    onClick={() => openModal(product.title)}
                    className={`group w-full bg-${index % 2 === 0 ? '[--primary-blue]' : '[--primary-red]'
                      } text-white font-medium px-4 py-2 rounded-md hover:bg-${index % 2 === 0 ? '[--primary-red]' : '[--primary-blue]'
                      } transition-all duration-300 hover:shadow-lg hover:shadow-[--primary-red]/30 focus:ring-2 focus:ring-[--primary-blue] focus:ring-offset-2 flex items-center justify-center`}
                    style={{ willChange: 'background-color, box-shadow' }}
                    aria-label={`Apply for ${product.title}`}
                  >
                    <span className="flex items-center text-sm uppercase tracking-wide">
                      Apply Now
                      <span className="ml-2 rounded-full bg-white border border-[--primary-blue] group-hover:border-[--primary-red] p-1 flex items-center justify-center">
                        <IconArrow className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300 text-[--primary-blue] group-hover:text-[--primary-blue]" />
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12 animate-fade-up" style={{ animationDelay: '1.2s' }}>
            <Link
              to="/products"
              className="inline-flex bg-[--primary-blue] text-white font-inter font-medium px-6 py-3 rounded-md hover:bg-[--primary-red] hover:shadow-lg hover:shadow-[--primary-red]/30 transition-all duration-300 uppercase tracking-wide text-sm focus:ring-2 focus:ring-[--primary-blue] focus:ring-offset-2"
              aria-label="View all products"
            >
              <span className="flex items-center">
                View All Products
                <span className="ml-2 rounded-full bg-white border border-[--primary-blue] group-hover:border-[--primary-red] p-1 flex items-center justify-center">
                  <IconArrow className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300 text-[--primary-blue] group-hover:text-[--primary-blue]" />
                </span>
              </span>
            </Link>
          </div>
        </div>
      </section>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-end z-50 animate-modal-in">
          <div
            ref={modalRef}
            className="bg-white/95 backdrop-blur-md rounded-lg p-8 w-full max-w-md mr-4 sm:mr-6"
            style={{ boxShadow: '0 4px 12px rgba(47, 73, 140, 0.3), 0 2px 8px rgba(132, 51, 69, 0.2)' }}
          >
            <div className="relative mb-6">
              <h3 className="text-xl font-inter font-bold text-[--primary-blue] tracking-tight text-center">
                Application Form
              </h3>
              <hr className="mt-2 mb-4 border-t-2 border-[--primary-blue] w-1/2 mx-auto" />
              <button
                onClick={closeModal}
                className="absolute top-0 right-0 text-[--primary-blue] hover:text-[--primary-red] focus:ring-2 focus:ring-[--primary-blue] focus:ring-offset-2 rounded-full"
                aria-label="Close modal"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>
            {showSuccessMessage ? (
              <div
                className="bg-green-100 text-green-800 p-4 rounded-lg mb-4 animate-fade-in"
                role="alert"
                aria-live="polite"
              >
                <h4 className="text-base font-inter font-semibold text-green-800">
                  Application Sent Successfully!
                </h4>
                <p className="text-sm font-inter text-green-700">
                  Thank you for your application. We’ll respond within 2 hours.
                </p>
              </div>
            ) : (
              <>
                {submitError && (
                  <div
                    className="bg-red-100 text-red-800 p-3 rounded-lg mb-4 animate-fade-in"
                    role="alert"
                    aria-live="polite"
                  >
                    {submitError}
                  </div>
                )}
                <p className="text-sm font-inter font-medium text-gray-600 mb-6 text-center">
                  Our team will respond within 2 hours.
                </p>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="mb-4">
                    <label
                      htmlFor="name"
                      className="block text-sm font-inter font-medium text-[--primary-blue] mb-1"
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.name ? 'border-[--primary-red]' : 'border-[--primary-blue]/30'
                        } rounded-md focus:outline-none focus:border-[--primary-blue] focus:ring-1 focus:ring-[--primary-blue]/50`}
                      required
                      aria-required="true"
                      aria-describedby={errors.name ? 'name-error' : undefined}
                    />
                    {errors.name && (
                      <p id="name-error" className="text-[--primary-red] text-xs mt-1" role="alert">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="service"
                      className="block text-sm font-inter font-medium text-[--primary-blue] mb-1"
                    >
                      Service Requested
                    </label>
                    <input
                      type="text"
                      id="service"
                      name="service"
                      value={formData.service}
                      readOnly
                      className="w-full px-4 py-2 border border-[--primary-blue]/30 rounded-md font-medium text-gray-800 bg-gray-50"
                      aria-describedby={errors.service ? 'service-error' : undefined}
                    />
                    {errors.service && (
                      <p id="service-error" className="text-[--primary-red] text-xs mt-1" role="alert">
                        {errors.service}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="email"
                      className="block text-sm font-inter font-medium text-[--primary-blue] mb-1"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.email ? 'border-[--primary-red]' : 'border-[--primary-blue]/30'
                        } rounded-md focus:outline-none focus:border-[--primary-blue] focus:ring-1 focus:ring-[--primary-blue]/50`}
                      required
                      aria-required="true"
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    {errors.email && (
                      <p id="email-error" className="text-[--primary-red] text-xs mt-1" role="alert">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="address"
                      className="block text-sm font-inter font-medium text-[--primary-blue] mb-1"
                    >
                      Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.address ? 'border-[--primary-red]' : 'border-[--primary-blue]/30'
                        } rounded-md focus:outline-none focus:border-[--primary-blue] focus:ring-1 focus:ring-[--primary-blue]/50`}
                      required
                      aria-required="true"
                      aria-describedby={errors.address ? 'address-error' : undefined}
                    />
                    {errors.address && (
                      <p id="address-error" className="text-[--primary-red] text-xs mt-1" role="alert">
                        {errors.address}
                      </p>
                    )}
                  </div>
                  <div className="mb-6">
                    <label
                      htmlFor="phone"
                      className="block text-sm font-inter font-medium text-[--primary-blue] mb-1"
                    >
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border ${errors.phone ? 'border-[--primary-red]' : 'border-[--primary-blue]/30'
                        } rounded-md focus:outline-none focus:border-[--primary-blue] focus:ring-1 focus:ring-[--primary-blue]/50`}
                      required
                      aria-required="true"
                      aria-describedby={errors.phone ? 'phone-error' : undefined}
                    />
                    {errors.phone && (
                      <p id="phone-error" className="text-[--primary-red] text-xs mt-1" role="alert">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="bg-gray-200 text-[--primary-blue] font-medium px-4 py-2 rounded-md hover:bg-gray-300 hover:text-[--primary-red] transition-all duration-300 uppercase tracking-wide text-sm focus:ring-2 focus:ring-[--primary-blue] focus:ring-offset-2"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-[--primary-blue] text-white font-medium px-4 py-2 rounded-md hover:bg-[--primary-red] hover:shadow-lg hover:shadow-[--primary-red]/30 transition-all duration-300 uppercase tracking-wide text-sm focus:ring-2 focus:ring-[--primary-blue] focus:ring-offset-2 flex items-center gap-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
      <style>
        {`
          :root {
            --primary-blue: #2F498C;
            --primary-red: #843345;
          }
          @keyframes fade-up {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-up {
            animation: fade-up 0.8s ease-in-out forwards;
            willChange: transform, opacity;
          }
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.8s ease-in-out forwards;
            willChange: opacity;
          }
          @keyframes modal-in {
            from { opacity: 0; transform: translateX(100%) scale(0.95); }
            to { opacity: 1; transform: translateX(0) scale(1); }
          }
          .animate-modal-in {
            animation: modal-in 0.3s ease-out forwards;
            willChange: transform, opacity;
          }
          .text-base {
            font-size: 1rem;
          }
          .backdrop-blur-md {
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
          }
          .aspect-[4/3] {
            aspect-ratio: 4 / 3;
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-fade-up, .animate-fade-in, .animate-modal-in, .group-hover\\:scale-105, .group-hover\\:brightness-105, .group-hover\\:opacity-50, .group-hover\\:translate-x-1, .group-hover\\:scale-x-100, .hover\\:shadow-lg, .hover\\:-translate-y-1 {
              animation: none !important;
              transform: none !important;
              opacity: 1 !important;
              transition: none !important;
            }
          }
          @media (max-width: 640px) {
            .aspect-[4/3] {
              max-height: 270px;
            }
          }
          @media (min-width: 641px) and (max-width: 1023px) {
            .aspect-[4/3] {
              max-height: 320px;
            }
          }
          @media (min-width: 1024px) {
            .aspect-[4/3] {
              max-height: 360px;
            }
          }
        `}
      </style>
    </>
  );
};

export default FeaturedProducts;

// 